import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { Article, getCategoryColor } from "@/data/articles";
import { Badge } from "@/components/ui/badge";
import { optimizeImageUrl } from "@/lib/image-utils";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "compact" | "featured";
  priority?: boolean;
}

const ArticleCard = ({ article, variant = "default", priority = false }: ArticleCardProps) => {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 py-4 border-b border-divider last:border-0 hover:bg-surface/50 transition-colors -mx-2 px-2 rounded"
      >
        <img
          src={optimizeImageUrl(article.imageUrl, { width: 200, quality: 70 })}
          alt={article.imageAlt}
          width={96}
          height={96}
          className="w-20 h-20 md:w-24 md:h-24 object-cover rounded flex-shrink-0"
          loading="lazy"
          decoding="async"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-block w-2 h-2 rounded-full ${getCategoryColor(article.category)}`} />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">{article.category}</span>
          </div>
          <h3 className="font-display text-sm md:text-base font-semibold text-foreground leading-snug line-clamp-2 group-hover:text-primary transition-colors">
            {article.title}
          </h3>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
            <span>{article.publicationDate}</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {article.readTime} min
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "featured") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block overflow-hidden rounded-lg bg-card border border-border hover:border-primary/30 transition-colors"
      >
        {/* Image with title overlay */}
        <div className="relative aspect-[16/9] overflow-hidden">
          <img
            src={optimizeImageUrl(article.imageUrl, { width: priority ? 900 : 600, quality: 75 })}
            alt={article.imageAlt}
            width={priority ? 900 : 600}
            height={priority ? 506 : 338}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "low"}
            decoding={priority ? "sync" : "async"}
            sizes={priority ? "(max-width: 768px) 100vw, (max-width: 1024px) 66vw, 840px" : "(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 560px"}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
            <div className="flex items-center gap-2 mb-1.5">
              <Badge variant="outline" className="text-xs border-primary/40 text-primary bg-background/80 backdrop-blur-sm">
                {article.category}
              </Badge>
              {article.isOpinion && (
                <Badge variant="outline" className="text-xs border-accent/40 text-accent bg-background/80 backdrop-blur-sm">
                  Opinion
                </Badge>
              )}
            </div>
            <h2 className="font-display text-base md:text-xl font-bold text-foreground leading-tight group-hover:text-primary transition-colors">
              {article.title}
            </h2>
          </div>
        </div>
        {/* Text below image */}
        <div className="p-4 md:p-5">
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3 max-w-2xl">
            {article.summary}
          </p>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="font-medium text-foreground/80">{article.author}</span>
            <span>·</span>
            <span>{article.publicationDate}</span>
            <span>·</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {article.readTime} min read
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block bg-card border border-border rounded-lg overflow-hidden hover:border-primary/30 transition-colors"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={optimizeImageUrl(article.imageUrl, { width: 500, quality: 75 })}
          alt={article.imageAlt}
          width={500}
          height={313}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
          decoding="async"
          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 410px"
        />
      </div>
      <div className="p-4 md:p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className={`inline-block w-2 h-2 rounded-full ${getCategoryColor(article.category)}`} />
          <span className="text-xs text-muted-foreground uppercase tracking-wider">{article.category}</span>
          {article.isOpinion && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-accent/40 text-accent">
              Opinion
            </Badge>
          )}
        </div>
        <h3 className="font-display text-base md:text-lg font-semibold text-foreground leading-snug line-clamp-2 group-hover:text-primary transition-colors mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
          {article.summary}
        </p>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span>{article.author}</span>
          <span>·</span>
          <span>{article.publicationDate}</span>
          <span className="flex items-center gap-1 ml-auto">
            <Clock className="w-3 h-3" />
            {article.readTime} min
          </span>
        </div>
      </div>
    </Link>
  );
};

export default ArticleCard;
